import { __exportAll } from "../_virtual/_rolldown/runtime.js";
import { BaseModelHelper } from "../helpers/base_model_helper.js";
import { BaseMongoHelper } from "../helpers/base_mongo_helper.js";
import { doesTimePassI9nAvailability } from "../helpers/i9n_availability_helper.js";
import "../helpers/index.js";
import _ from "underscore";
import { FulfillmentMethodHelper, ItemHiddenState } from "@biteinc/enums";
import { UniversalCompareHelper, UniversalTime } from "@biteinc/helpers-universal";
//#region src/services/core_timing_service.ts
var core_timing_service_exports = /* @__PURE__ */ __exportAll({
	filterInEnabledFulfillmentMethods: () => filterInEnabledFulfillmentMethods,
	findClosestStructuresAndActiveTimings: () => findClosestStructuresAndActiveTimings,
	getFulfillmentMethodsFromStructures: () => getFulfillmentMethodsFromStructures,
	getTimetablesAndOverrides: () => getTimetablesAndOverrides,
	pickStructuresForActiveTimings: () => pickStructuresForActiveTimings,
	processOpenHours: () => processOpenHours,
	removeClosedOrDisabledFulfillmentMethods: () => removeClosedOrDisabledFulfillmentMethods,
	updateActiveDayParts: () => updateActiveDayParts
});
const structureProps = [
	"_id",
	"menuTimingId",
	"fulfillmentMethods"
];
function sortedActiveTimings(time, timezone, timings, structures) {
	const structuresByTimingId = BaseModelHelper.groupDataBy(structures, "menuTimingId");
	const activeTimings = timings.filter((timing) => {
		return timing.isActiveAt(time, timezone);
	});
	const weekTimestamp = UniversalTime.weekTimestamp(time, timezone);
	return activeTimings.sort((a, b) => {
		let res = UniversalCompareHelper.descBool(!!a.data.dateRange, !!b.data.dateRange);
		if (res !== 0) return res;
		res = UniversalCompareHelper.ascBool(!!structuresByTimingId[`${a.id}`], !!structuresByTimingId[`${b.id}`]);
		if (res !== 0) return res;
		const rangeA = a.rangeThatIsActiveAt(weekTimestamp);
		const rangeB = b.rangeThatIsActiveAt(weekTimestamp);
		res = UniversalCompareHelper.desc(rangeA.from, rangeB.from);
		if (res !== 0) return res;
		const rangeALength = rangeA.to - rangeA.from;
		const rangeBLength = rangeB.to - rangeB.from;
		return UniversalCompareHelper.asc(rangeALength, rangeBLength);
	});
}
function pickStructuresForActiveTimings(activeTimings, structures) {
	const structuresByTimingId = BaseModelHelper.groupBy(structures, "menuTimingId");
	const structTimings = activeTimings.filter((timing) => {
		return !!structuresByTimingId[timing.id.toString()];
	});
	if (structTimings.length) return structuresByTimingId[`${structTimings[0].id}`];
	return [structures[0]];
}
async function findClosestStructuresAndActiveTimings(modelFactory, locationId, time, timezone, fulfillmentMethod) {
	const MenuStructure = modelFactory.getMenuStructureClass();
	const MenuTiming = modelFactory.getMenuTimingClass();
	const [allStructures, timings] = await Promise.all([MenuStructure.find({
		locationId,
		menuTimingId: { $ne: null },
		...fulfillmentMethod && { fulfillmentMethods: fulfillmentMethod }
	}), MenuTiming.find({ locationId })]);
	const activeTimings = sortedActiveTimings(time, timezone, timings, allStructures.map((structure) => structure.data));
	return {
		structures: pickStructuresForActiveTimings(activeTimings, allStructures),
		activeMenuTimingIds: BaseModelHelper.idArrayFromModels(activeTimings)
	};
}
function findActiveModels(structures, timings, time, timezone) {
	const activeTimings = sortedActiveTimings(time, timezone, timings, structures);
	const activeTimingById = BaseModelHelper.idMap(activeTimings);
	return {
		activeTimings,
		activeTimingById,
		activeStructures: structures.filter((structure) => {
			return !!activeTimingById[structure.menuTimingId.toString()];
		})
	};
}
function filterInEnabledFulfillmentMethods(location, fulfillmentMethods) {
	return fulfillmentMethods.filter((fulfillmentMethod) => {
		return location.getDiningOption(fulfillmentMethod)?.isEnabled;
	});
}
async function getTimetablesAndOverrides(modelFactory, locationId, fulfillmentMethods) {
	const OpenHoursOverride = modelFactory.getOpenHoursOverrideClass();
	const OpenHoursTimetable = modelFactory.getOpenHoursTimetableClass();
	const [timetables, overrides] = await Promise.all([OpenHoursTimetable.find({
		locationId,
		fulfillmentMethod: { $in: fulfillmentMethods }
	}), OpenHoursOverride.find({
		locationId,
		fulfillmentMethod: { $in: fulfillmentMethods }
	})]);
	return {
		timetables,
		overrides
	};
}
/**
* @returns True if the timetables and overrides allow the store to be open
*/
function processOpenHours(time, timezone, timetable, overrides = []) {
	overrides.forEach((override) => {
		if (timetable.data.fulfillmentMethod !== override.data.fulfillmentMethod) throw new Error("Cannot process open hours with different fulfillment methods.");
	});
	const startOfDay = UniversalTime.getStartOfDayFromUnixTime(time, timezone);
	const dateOverrides = overrides.filter((override) => {
		return override.data.date === startOfDay;
	});
	if (!dateOverrides.length) return timetable.isActiveAt(time, timezone);
	dateOverrides.forEach((override) => {
		if (override.data.isOpen !== dateOverrides[0].data.isOpen) throw new Error("Cannot process open and closed overrides.");
	});
	const hasOverride = dateOverrides.some((override) => {
		return override.isActiveAt(time);
	});
	return !!dateOverrides[0].data.isOpen === hasOverride;
}
function removeClosedOrDisabledFulfillmentMethods(location, enabledFulfillmentMethods, timetables, overrides, time) {
	const timetableByFulfillmentMethod = BaseModelHelper.map(timetables, "fulfillmentMethod");
	const overridesByFulfillmentMethod = BaseModelHelper.groupBy(overrides, "fulfillmentMethod");
	return enabledFulfillmentMethods.filter((fulfillmentMethod) => {
		return processOpenHours(time, location.data.timezone, timetableByFulfillmentMethod[`${fulfillmentMethod}`], overridesByFulfillmentMethod[`${fulfillmentMethod}`]);
	});
}
function getFulfillmentMethodsFromStructures(structures) {
	const flatFulfillmentMethods = _.flatten(structures.map((activeStructure) => {
		return activeStructure.fulfillmentMethods;
	}));
	return _.unique(flatFulfillmentMethods.sort());
}
function haveKiosksBeenOpenForAnHour(location, structures, timings, timetables, overrides, time, timezone) {
	function isFulfillmentMethodKioskAndEnabled(fulfillmentMethod) {
		return !!(FulfillmentMethodHelper.isKiosk(fulfillmentMethod) && location.getDiningOption(fulfillmentMethod)?.isEnabled);
	}
	const kioskStructures = structures.filter((structure) => {
		return structure.fulfillmentMethods.some(isFulfillmentMethodKioskAndEnabled);
	});
	let timeI = time;
	let kiosksHaveBeenOpenForAnHour = true;
	let i = 0;
	while (i < 4 && kiosksHaveBeenOpenForAnHour) {
		timeI -= UniversalTime.MINUTE * 15;
		const { activeStructures: activeKioskStructures } = findActiveModels(kioskStructures, timings, timeI, timezone);
		const activeFulfillmentMethods = removeClosedOrDisabledFulfillmentMethods(location, getFulfillmentMethodsFromStructures(activeKioskStructures).filter(isFulfillmentMethodKioskAndEnabled), timetables, overrides, timeI);
		kiosksHaveBeenOpenForAnHour &&= !!activeFulfillmentMethods.length;
		++i;
	}
	return kiosksHaveBeenOpenForAnHour;
}
/**
* Round up if we're close to the next interval so that if the refresh runs earlier than expected,
* we still open/close at roughly the right time.
*
* Otherwise, round down since we do not want to open/close a location too early.
*/
function roundToBest15MinuteInterval(time) {
	const nextFifteenMinuteInterval = UniversalTime.roundUnixTimeToNext15(time);
	if (nextFifteenMinuteInterval - time <= 2 * UniversalTime.MINUTE) return nextFifteenMinuteInterval;
	const fifteenMinutesAgo = time - 15 * UniversalTime.MINUTE;
	return UniversalTime.roundUnixTimeToNext15(fifteenMinutesAgo);
}
async function findActiveMenuTimings(modelFactory, location, time) {
	const MenuStructure = modelFactory.getMenuStructureClass();
	const MenuTiming = modelFactory.getMenuTimingClass();
	const locationId = location.id;
	const timezone = location.data.timezone;
	const [allStructures, timings] = await Promise.all([MenuStructure.findProjectedData({
		locationId,
		menuTimingId: { $ne: null }
	}, structureProps), MenuTiming.find({ locationId })]);
	const roundedTimestamp = roundToBest15MinuteInterval(time);
	const { activeTimings, activeTimingById, activeStructures } = findActiveModels(allStructures, timings, roundedTimestamp, timezone);
	const allEnabledFulfillmentMethods = filterInEnabledFulfillmentMethods(location, getFulfillmentMethodsFromStructures(allStructures));
	const { timetables, overrides } = await getTimetablesAndOverrides(modelFactory, location.id, allEnabledFulfillmentMethods);
	const activeFulfillmentMethods = removeClosedOrDisabledFulfillmentMethods(location, filterInEnabledFulfillmentMethods(location, getFulfillmentMethodsFromStructures(activeStructures)), timetables, overrides, time);
	const activeKioskFulfillmentMethods = activeFulfillmentMethods.filter((activeFulfillmentMethod) => {
		return FulfillmentMethodHelper.isKiosk(activeFulfillmentMethod);
	});
	const activeKioskTimingIdStrs = activeStructures.filter((structure) => {
		return _.intersection(structure.fulfillmentMethods, activeKioskFulfillmentMethods).length > 0;
	}).map((structure) => {
		return structure.menuTimingId.toString();
	});
	return {
		activeTimings,
		activeKioskTimings: _.unique(activeKioskTimingIdStrs).map((activeKioskTimingIdStr) => {
			return activeTimingById[activeKioskTimingIdStr];
		}),
		activeFulfillmentMethods,
		kiosksHaveBeenOpenForAnHour: haveKiosksBeenOpenForAnHour(location, allStructures, timings, timetables, overrides, roundedTimestamp, timezone)
	};
}
/**
* @returns true iff the `i9nIsAvailable` property of any `MenuItem` has changed
*/
async function updateActiveItemI9nAvailabilities(modelFactory, location, time, dryRun) {
	const MenuItem = modelFactory.getMenuItemClass();
	const ops = [];
	await MenuItem.forEachLimit({
		locationId: location.id,
		i9nAvailability: { $exists: true }
	}, { i9nAvailability: true }, 100, async (menuItem) => {
		const shouldMenuItemBeAvailable = doesTimePassI9nAvailability(menuItem.data.i9nAvailability ?? {
			dates: [],
			days: []
		}, time, location.data.timezone);
		if (menuItem.data.i9nAvailability.isAvailable !== shouldMenuItemBeAvailable) ops.push({ updateOne: {
			filter: { _id: menuItem.id },
			update: { $set: { "i9nAvailability.isAvailable": shouldMenuItemBeAvailable } }
		} });
	});
	if (!dryRun && ops.length) await MenuItem.bulkWrite(ops);
	return !!ops.length;
}
/**
* @returns true if a menu model has an active timing which has changed state, meaning that the menu
* model may have just become available or unavailable.
*
* This isn't flawless, since a menu model may be assigned to two continuous timings, where one
* timing ends and the other begins. This function will return true in that case, even though the
* availability state of the menu model hasn't changed.
*
* This also doesn't account for many cases where the model wouldn't be present on the menu, such
* as models removed automatically due to min/max constraints or menu items on hidden sections.
*
* An alternative approach would be to send all models with assigned timings to the clients, and let
* the client decide which models are available based on the system time. This would avoid
* unnecessary fetches, as we would know the client already has all of the needed models for any
* given time.
*/
async function doMenuModelsHaveChangedActiveTimings(modelFactory, locationId, changedTimingIds) {
	if (!changedTimingIds.length) return false;
	const MenuItem = modelFactory.getMenuItemClass();
	const MenuSection = modelFactory.getMenuSectionClass();
	const ModGroup = modelFactory.getModGroupClass();
	const [menuItem, menuSection, modGroup] = await Promise.all([
		MenuItem.findOneProjectedData({
			"activeTimings._id": { $in: changedTimingIds },
			locationId,
			archivedAt: { $exists: false },
			priceOptions: { $elemMatch: {
				hiddenState: { $nin: [ItemHiddenState.HiddenIndefinitely, ItemHiddenState.HiddenUntilTomorrow] },
				i9nHiddenState: { $nin: [ItemHiddenState.HiddenIndefinitely, ItemHiddenState.HiddenUntilTomorrow] }
			} }
		}, ["_id"]),
		MenuSection.findOneProjectedData({
			"activeTimings._id": { $in: changedTimingIds },
			locationId,
			archivedAt: { $exists: false },
			hideFromMenu: { $ne: true }
		}, ["_id"]),
		ModGroup.findOneProjectedData({
			"activeTimings._id": { $in: changedTimingIds },
			locationId,
			archivedAt: { $exists: false },
			hideFromMenu: { $ne: true }
		}, ["_id"])
	]);
	return !!(menuItem || menuSection || modGroup);
}
/**
* @description Finds the closest active day parts to the specified time and updates the menu
* and location accordingly should the active day parts change.
* @returns Booleans dictating some properties of the expected state of the menu and location
*/
async function updateActiveDayParts(modelFactory, location, menu, time, dryRun) {
	const { activeTimings, activeKioskTimings, activeFulfillmentMethods, kiosksHaveBeenOpenForAnHour } = await findActiveMenuTimings(modelFactory, location, time);
	const activeKioskTimingIds = BaseModelHelper.idArrayFromModels(activeKioskTimings);
	const activeKioskTimingIdsStrSet = new Set(activeKioskTimingIds.map((kioskTimingId) => {
		return kioskTimingId.toString();
	}));
	const oldActiveKioskTimingIds = menu.data.activeMenuTimingIds.filter((activeMenuTimingId) => {
		return activeKioskTimingIdsStrSet.has(activeMenuTimingId.toString());
	});
	const hasNewActiveKioskTimingIds = !(BaseMongoHelper.idArraysIntersectionSize(activeKioskTimingIds, oldActiveKioskTimingIds) === activeKioskTimingIds.length);
	const activeMenuTimingIds = BaseModelHelper.idArrayFromModels(activeTimings);
	const oldActiveTimingIdStrSet = new Set(menu.data.activeMenuTimingIds.map((id) => id.toString()));
	const newActiveTimingIdStrSet = new Set(activeMenuTimingIds.map((id) => id.toString()));
	const changedTimingIds = [...activeMenuTimingIds.filter((id) => !oldActiveTimingIdStrSet.has(id.toString())), ...menu.data.activeMenuTimingIds.filter((id) => !newActiveTimingIdStrSet.has(id.toString()))];
	const hasNewActiveMenuTimingIds = hasNewActiveKioskTimingIds || Boolean(changedTimingIds.length);
	const activeFulfillmentMethodsMatch = _.isEqual(activeFulfillmentMethods, location.data.activeFulfillmentMethods);
	const newActiveKioskFulfillmentMethods = activeFulfillmentMethods.filter((activeFulfillmentMethod) => {
		return FulfillmentMethodHelper.isKiosk(activeFulfillmentMethod);
	});
	const kiosksShouldBeClosed = !location.hasAssignedFutureOrderingKioskDiningOption() && !newActiveKioskFulfillmentMethods.length;
	const currentActiveKioskFulfillmentMethods = location.data.activeFulfillmentMethods?.filter((activeFulfillmentMethod) => {
		return FulfillmentMethodHelper.isKiosk(activeFulfillmentMethod);
	}) || [];
	const haveKioskActiveFulfillmentMethodsChanged = !_.isEqual(newActiveKioskFulfillmentMethods, currentActiveKioskFulfillmentMethods);
	if (!dryRun && hasNewActiveMenuTimingIds) await menu.setWithDict({ activeMenuTimingIds });
	const locationSetDict = { ...!activeFulfillmentMethodsMatch && { activeFulfillmentMethods } };
	if (!dryRun && _.size(locationSetDict)) await location.setWithDict(locationSetDict);
	const hasItemI9nAvailabilityChanged = await updateActiveItemI9nAvailabilities(modelFactory, location, time, dryRun);
	const menuModelsHaveChangedActiveTimings = await doMenuModelsHaveChangedActiveTimings(modelFactory, location.id, changedTimingIds);
	const shouldNotifyKiosks = hasNewActiveKioskTimingIds || haveKioskActiveFulfillmentMethodsChanged;
	return {
		shouldNotifyKiosks,
		shouldMarkMenuAsUpdated: shouldNotifyKiosks || hasItemI9nAvailabilityChanged || menuModelsHaveChangedActiveTimings,
		kiosksShouldBeClosed,
		shouldInvalidateCache: hasNewActiveMenuTimingIds,
		kiosksHaveBeenOpenForAnHour
	};
}
//#endregion
export { core_timing_service_exports, filterInEnabledFulfillmentMethods, findClosestStructuresAndActiveTimings, getFulfillmentMethodsFromStructures, getTimetablesAndOverrides, pickStructuresForActiveTimings, processOpenHours, removeClosedOrDisabledFulfillmentMethods, updateActiveDayParts };

//# sourceMappingURL=core_timing_service.js.map